import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getFavorites, getVineyard, getWine, removeFavorite } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { StarRating, WineScore } from '../components/StarRating';
import { toast } from 'sonner';
import { Heart, MapPin, Wine, Grape, Trash2 } from 'lucide-react';

const Favorites = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [vineyardFavorites, setVineyardFavorites] = useState([]);
  const [wineFavorites, setWineFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, authLoading]);

  const loadFavorites = async () => {
    setLoading(true);
    try {
      const favorites = await getFavorites();
      
      // Load vineyard details
      const vineyardFavs = favorites.filter(f => f.item_type === 'vineyard');
      const vineyardDetails = await Promise.all(
        vineyardFavs.map(async (fav) => {
          try {
            const vineyard = await getVineyard(fav.item_id);
            return { ...fav, details: vineyard };
          } catch {
            return null;
          }
        })
      );
      setVineyardFavorites(vineyardDetails.filter(Boolean));

      // Load wine details
      const wineFavs = favorites.filter(f => f.item_type === 'wine');
      const wineDetails = await Promise.all(
        wineFavs.map(async (fav) => {
          try {
            const wine = await getWine(fav.item_id);
            return { ...fav, details: wine };
          } catch {
            return null;
          }
        })
      );
      setWineFavorites(wineDetails.filter(Boolean));
    } catch (error) {
      console.error('Failed to load favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (itemId, type) => {
    try {
      await removeFavorite(itemId);
      toast.success('Removed from favorites');
      if (type === 'vineyard') {
        setVineyardFavorites(prev => prev.filter(f => f.item_id !== itemId));
      } else {
        setWineFavorites(prev => prev.filter(f => f.item_id !== itemId));
      }
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-8 pb-20" data-testid="favorites-page">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#d4af37] mb-2">Your Collection</p>
          <h1 className="text-4xl md:text-5xl font-serif text-stone-50">Favorites</h1>
        </div>

        <Tabs defaultValue="vineyards" className="w-full">
          <TabsList className="bg-stone-900/50 border border-stone-700 p-1 mb-8">
            <TabsTrigger
              value="vineyards"
              className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-stone-950"
              data-testid="tab-vineyards"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Vineyards ({vineyardFavorites.length})
            </TabsTrigger>
            <TabsTrigger
              value="wines"
              className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-stone-950"
              data-testid="tab-wines"
            >
              <Wine className="w-4 h-4 mr-2" />
              Wines ({wineFavorites.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="vineyards">
            {vineyardFavorites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {vineyardFavorites.map((fav) => (
                  <div
                    key={fav.id}
                    className="vineyard-card group relative overflow-hidden bg-stone-900/40 border border-white/5"
                    data-testid={`favorite-vineyard-${fav.item_id}`}
                  >
                    <Link to={`/vineyards/${fav.item_id}`}>
                      <div className="aspect-[4/3] img-zoom">
                        <img
                          src={fav.details.photos?.[0] || 'https://images.unsplash.com/photo-1695700762236-3c61c5026858?w=600&q=80'}
                          alt={fav.details.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone-500 mb-2">
                          <MapPin className="w-3 h-3" />
                          {fav.details.region}, {fav.details.country}
                        </div>
                        <h3 className="text-xl font-serif text-stone-50 mb-2 group-hover:text-[#d4af37] transition-colors">
                          {fav.details.name}
                        </h3>
                        <p className="text-sm text-stone-500 mb-4">{fav.details.producer}</p>
                        <StarRating rating={fav.details.average_rating || 0} readonly size="sm" />
                      </div>
                    </Link>
                    <button
                      onClick={() => handleRemoveFavorite(fav.item_id, 'vineyard')}
                      className="absolute top-4 right-4 p-2 bg-black/50 backdrop-blur-sm rounded-full text-red-400 hover:bg-red-600 hover:text-white transition-colors"
                      data-testid={`remove-vineyard-${fav.item_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Heart className="w-16 h-16 text-stone-700 mx-auto mb-6" />
                <h3 className="text-2xl font-serif text-stone-400 mb-2">No Favorite Vineyards</h3>
                <p className="text-stone-600 mb-6">
                  Start exploring and save your favorite vineyards!
                </p>
                <Button
                  onClick={() => navigate('/vineyards')}
                  className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none"
                  data-testid="explore-vineyards-btn"
                >
                  Explore Vineyards
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="wines">
            {wineFavorites.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {wineFavorites.map((fav) => (
                  <div
                    key={fav.id}
                    className="wine-card group relative p-6 bg-stone-900/20 hover:bg-stone-900/60 border border-white/5 transition-all"
                    data-testid={`favorite-wine-${fav.item_id}`}
                  >
                    <Link to={`/wines/${fav.item_id}`}>
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#d4af37] mb-2">
                            <Grape className="w-3 h-3" />
                            {fav.details.grape_variety}
                          </div>
                          <h3 className="text-lg font-serif text-stone-50 group-hover:text-[#d4af37] transition-colors">
                            {fav.details.name}
                          </h3>
                        </div>
                        {fav.details.average_rating > 0 && (
                          <WineScore score={fav.details.average_rating} size="sm" />
                        )}
                      </div>
                      <p className="text-sm text-stone-500">{fav.details.vineyard_name}</p>
                      {fav.details.vintage && (
                        <p className="text-xs text-stone-600 mt-1">Vintage {fav.details.vintage}</p>
                      )}
                    </Link>
                    <button
                      onClick={() => handleRemoveFavorite(fav.item_id, 'wine')}
                      className="absolute top-4 right-4 p-2 bg-black/50 backdrop-blur-sm rounded-full text-red-400 hover:bg-red-600 hover:text-white transition-colors"
                      data-testid={`remove-wine-${fav.item_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Heart className="w-16 h-16 text-stone-700 mx-auto mb-6" />
                <h3 className="text-2xl font-serif text-stone-400 mb-2">No Favorite Wines</h3>
                <p className="text-stone-600 mb-6">
                  Discover wines and add them to your collection!
                </p>
                <Button
                  onClick={() => navigate('/wines')}
                  className="bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none"
                  data-testid="explore-wines-btn"
                >
                  Explore Wines
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Favorites;
